import * as path from 'path';

import { AssetPath, loadProjectSettings } from '@gnsx/genesys.js';
import {
  GAME_PROJECT_FILE_EXT,
  PROJECT_PATH_PREFIX,
} from '@gnsx/genesys.js';

import type { IStorageProvider } from '@/lib/storage/interfaces';

interface UpdateProjectSettingsOptions {
  /**
   * if true, the new settings will be merged with the existing settings, otherwise the existing settings will be replaced
   */
  merge?: boolean;

  /**
   * if provided, this will be used as the project file to update, otherwise it will use the existing project file
   */
  projectFile?: string;

  /**
   * if the project file does not exist, create it with this name
   * if not provided, it will not create a new project file
   */
  projectNameToCreateIfNotExist?: string | (() => string);
}

/**
 * update the project settings in the .genesys-project file, this can only happen in the editor, as against to the built project
 *
 * @param storageProvider the storage provider to use
 * @param settings setting entries to update in the project file
 * @param options options for the update
 * @returns success or not
 */
export const updateProjectSettings = async (storageProvider: IStorageProvider, settings: Record<string, any>, options?: UpdateProjectSettingsOptions): Promise<boolean> => {
  let [projectFile, projectSettings] = await loadProjectSettings(storageProvider, options?.projectFile ?? '');
  if (!projectFile) {
    if (options?.projectNameToCreateIfNotExist) {
      const projectName = typeof options.projectNameToCreateIfNotExist === 'function' ? options.projectNameToCreateIfNotExist() : options.projectNameToCreateIfNotExist;
      projectFile = path.join(PROJECT_PATH_PREFIX, `${projectName}${GAME_PROJECT_FILE_EXT}`);
    }
    else {
      console.warn('updateProjectSettings: No project file found, cannot update settings');
      return false;
    }
  }

  // Merge new settings with existing ones
  const updatedSettings = options?.merge ? { ...projectSettings, ...settings } : settings;
  try {
    // Upload the updated settings back to the project file
    await storageProvider.uploadFile(AssetPath.fromString(projectFile), new Blob([JSON.stringify(updatedSettings, null, 2)], { type: 'application/json' }));
    console.log('Project settings updated successfully:', projectFile);
  } catch (error) {
    console.error('Failed to update project settings:', error);
    return false;
  }
  return true;
};